import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import _ from 'lodash';

import '@/lib/event-target';
import { DropzoneInputContext } from './dropzone-input.context';

export type DropzoneEvent<T extends File> = CustomEvent<{ acceptedFiles: T[] }>;
export type DropzoneListener<T extends File> = (event: DropzoneEvent<T>) => void;

export interface DropzoneContextType {
  inputRef: React.RefObject<HTMLInputElement>;
  isDragActive: boolean;
  addListener: <T extends File>(listener: DropzoneListener<T>) => void;
  removeListener: <T extends File>(listener: DropzoneListener<T>) => void;
}

export const DropzoneContext = React.createContext<DropzoneContextType>(null);

export const DropzoneRootWrapper = ({ children }: { children: React.ReactChild }) => {
  const events = useMemo(() => new EventTarget(), []);
  const onDrop = useCallback((acceptedFiles) => {
    events.dispatchEvent(new CustomEvent('drop-files', { detail: { acceptedFiles } }));
  }, []);
  const addListener = useCallback(
    ((listener) => events.addEventListener('drop-files', listener)) as DropzoneContextType['addListener'],
    [],
  );
  const removeListener = useCallback(
    ((listener) => events.removeEventListener('drop-files', listener)) as DropzoneContextType['removeListener'],
    [],
  );

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({ onDrop });
  const dropzoneCtxValue = useMemo(
    () => ({ isDragActive, inputRef, addListener, removeListener }),
    [isDragActive],
  );

  const [onlyChild] = React.Children.toArray(children);
  const rootProps = _.omit(getRootProps(), ['onClick', 'onKeyDown', 'onFocus', 'onBlur']);
  const root = React.cloneElement(onlyChild as React.ReactElement, rootProps);

  return (
    <DropzoneContext.Provider value={dropzoneCtxValue}>
      <DropzoneInputContext.Provider value={getInputProps()}>
        {root}
      </DropzoneInputContext.Provider>
    </DropzoneContext.Provider>
  );
};
